import { createContext, useCallback, useContext, useState, type ReactNode } from 'react';
import { useConnect, useConnectors } from 'wagmi';
import { useI18n } from '../../i18n/index.tsx';
import { Icon } from '../ui/Icon.tsx';
import { Modal, Notice } from '../ui/primitives.tsx';
import { explainError } from '../tx/errors.ts';
import { isMobileBrowser, walletAppLinks } from '../../lib/walletLinks.ts';

const Ctx = createContext<() => void>(() => {});
export const useConnectModal = () => useContext(Ctx);

export function ConnectModalProvider({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false);
  const show = useCallback(() => setOpen(true), []);
  return (
    <Ctx.Provider value={show}>
      {children}
      {open ? <ConnectModal onClose={() => setOpen(false)} /> : null}
    </Ctx.Provider>
  );
}

function ConnectModal({ onClose }: { onClose(): void }) {
  const { t } = useI18n();
  const connectors = useConnectors();
  const { connect, error, isPending } = useConnect();
  // EIP-6963 wallets announce themselves by name; the generic "Injected" entry only matters when none did.
  const announced = connectors.filter((c) => c.type === 'injected' && c.id !== 'injected');
  const injected =
    announced.length > 0 ? announced : connectors.filter((c) => c.id === 'injected' && typeof window !== 'undefined' && 'ethereum' in window);
  const list = [...injected, ...connectors.filter((c) => c.type === 'walletConnect' || c.type === 'mock')];
  // a phone browser without an injected wallet: offer to reopen this page inside a wallet app's browser
  const appLinks =
    injected.length === 0 && typeof window !== 'undefined' && isMobileBrowser(navigator.userAgent)
      ? walletAppLinks(window.location.href)
      : [];
  return (
    <Modal title={t('wallet.choose')} onClose={onClose}>
      <div className="wallet-list">
        {list.map((c) => (
          <button
            key={c.uid}
            type="button"
            className="wallet-option"
            disabled={isPending}
            onClick={() => connect({ connector: c }, { onSuccess: onClose })}
          >
            {c.icon ? <img src={c.icon} alt="" /> : <Icon name="wallet" width={22} height={22} />}
            {c.type === 'walletConnect' ? t('wallet.walletconnect') : c.type === 'mock' ? 'Test wallet (local)' : c.name}
          </button>
        ))}
        {appLinks.length > 0 ? <p className="muted" style={{ fontSize: 12, margin: '6px 0 0' }}>{t('wallet.mobileHint')}</p> : null}
        {appLinks.map((l) => (
          <a key={l.name} className="wallet-option" href={l.href} rel="noopener noreferrer">
            <Icon name="wallet" width={22} height={22} />
            {t('wallet.openIn', { wallet: l.name })}
          </a>
        ))}
        {list.length === 0 && appLinks.length === 0 ? <Notice tone="warn">{t('wallet.none')}</Notice> : null}
        {error ? <Notice tone="error">{explainError(error, t)}</Notice> : null}
        <p className="muted" style={{ fontSize: 12, margin: '6px 0 0' }}>
          {t('wallet.safety')}
        </p>
      </div>
    </Modal>
  );
}
